import { useMutation } from '@apollo/client';
import type { DisconnectSlackMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DisconnectSlackDocument,
  GetNotificationPreferencesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useDisconnectSlackTRPC } from './useDisconnectSlackTRPC';

/**
 * Flag-gated wrapper for disconnecting the current user's Slack account in
 * Knock. Consumers import THIS, never the `*TRPC` twin (see
 * packages/web/CLAUDE.md — Mutation Hook Wrapper Pattern).
 */
export const useDisconnectSlack = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [disconnectGraphQL, graphqlState] = useMutation(
    DisconnectSlackDocument,
    {
      refetchQueries: [GetNotificationPreferencesDocument],
    }
  );

  const trpcMutation = useDisconnectSlackTRPC();

  const disconnectSlack = async (): Promise<DisconnectSlackMutation> => {
    if (trpcEnabled) {
      return trpcMutation.disconnectSlack();
    }

    const result = await disconnectGraphQL();
    if (!result.data) {
      throw new Error('Failed to disconnect Slack');
    }

    return result.data;
  };

  return {
    disconnectSlack,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
